import { useCallback } from 'react'
import { AppConstants } from '@/lib/constants/constants'

/**
 * Retries a failing async function with exponential backoff.
 *
 * Designed to recover from transient backend errors (e.g. 500 Bad Gateway)
 * that resolve on their own within a few seconds. The caller only sees an
 * error if every attempt fails; intermediate failures are invisible to the UI.
 */

type RetryOptions = {
	maxRetries?: number
	baseDelay?: number
	// Called after each failed attempt, before the next retry. Use this to
	// push analytics events scoped to a specific feature — keeping each
	// caller's retry tracking separate (e.g. MLS search vs. pipeline).
	onRetry?: (attempt: number) => void
}

export const useRetry = () => {
	const retryWithBackoff = useCallback(async <T>(fn: () => Promise<T>, options?: RetryOptions): Promise<T> => {
		const {
			maxRetries = AppConstants.RETRY_MAX_ATTEMPTS,
			baseDelay = AppConstants.RETRY_BASE_DELAY_MS,
			onRetry,
		} = options ?? {}
		let lastError: Error | unknown

		for (let attempt = 0; attempt <= maxRetries; attempt++) {
			try {
				// eslint-disable-next-line no-await-in-loop
				const result = await fn()

				return result
			} catch (error) {
				lastError = error
				console.error(`Attempt ${attempt + 1} failed:`, error)

				onRetry?.(attempt + 1)

				if (attempt === maxRetries) {
					console.error(`All ${maxRetries} attempts failed`)
					break
				}

				const delay = baseDelay * 2 ** attempt

				// eslint-disable-next-line no-await-in-loop
				await new Promise<void>(resolve => {
					setTimeout(resolve, delay)
				})
			}
		}

		throw lastError
	}, [])

	return { retryWithBackoff }
}
